// Navbar.js
import React, { useState, useEffect } from 'react';
import {
  AppBar, Toolbar, Typography, Box, IconButton,
  Drawer, List, ListItem, ListItemText, Container,
  useTheme, useMediaQuery,
} from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

const NAV_ITEMS = [
  { title: 'Vote', path: '/' },
  { title: 'Leaderboard', path: '/leaderboard' },
  { title: 'Previous Seasons', path: '/seasons' },
  { title: '2026 World Cup', path: '/world-cup-2026' },
  { title: 'Privacy', path: '/privacy-policy' },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDark = location.pathname === '/world-cup-2026';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: isDark
            ? (scrolled ? 'rgba(10,10,10,0.95)' : 'transparent')
            : (scrolled ? 'rgba(250,249,247,0.97)' : 'transparent'),
          backdropFilter: scrolled ? 'blur(16px)' : 'none',
          borderBottom: scrolled ? '1px solid rgba(120,100,80,0.12)' : 'none',
          transition: 'background-color 0.25s, border-color 0.25s',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ justifyContent: 'space-between', py: 0.5 }}>
            <Typography
              component={RouterLink}
              to="/"
              variant="h6"
              sx={{
                textDecoration: 'none',
                fontWeight: 800,
                fontSize: { xs: '1rem', sm: '1.1rem' },
                letterSpacing: '-0.02em',
                color: isDark ? '#fff' : 'text.primary',
                display: 'flex',
                alignItems: 'baseline',
                gap: '1px',
              }}
            >
              <Box component="span" sx={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: { xs: '1.5rem', sm: '1.65rem' }, letterSpacing: '0.03em' }}>
                Fallon{' '}
              </Box>
              <Box component="span" sx={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: { xs: '1.5rem', sm: '1.65rem' }, color: 'primary.main', letterSpacing: '0.03em' }}>d&apos;</Box>
              <Box component="span" sx={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: { xs: '1.5rem', sm: '1.65rem' }, letterSpacing: '0.03em' }}>Floor</Box>
            </Typography>

            {!isMobile && (
              <Box component="nav" sx={{ display: 'flex', gap: 0.5 }}>
                {NAV_ITEMS.map(({ title, path }) => (
                  <Box
                    key={path}
                    component={RouterLink}
                    to={path}
                    sx={{
                      textDecoration: 'none',
                      color: isActive(path) ? (isDark ? '#fff' : 'text.primary') : (isDark ? 'rgba(255,255,255,0.55)' : 'text.secondary'),
                      fontWeight: isActive(path) ? 600 : 400,
                      fontSize: '0.82rem',
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      px: 1.5,
                      py: 0.75,
                      borderBottom: '2px solid',
                      borderBottomColor: isActive(path) ? 'primary.main' : 'transparent',
                      transition: 'color 0.15s, border-color 0.15s',
                      '&:hover': { color: isDark ? '#fff' : 'text.primary' },
                    }}
                  >
                    {title}
                  </Box>
                ))}
              </Box>
            )}

            {isMobile && (
              <IconButton
                onClick={() => setDrawerOpen(true)}
                sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : 'text.secondary', '&:hover': { color: isDark ? '#fff' : 'text.primary' } }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 260,
            bgcolor: '#FAF9F7',
            borderLeft: '1px solid rgba(120,100,80,0.12)',
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1.5 }}>
          <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: 'text.secondary' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <List disablePadding>
          {NAV_ITEMS.map(({ title, path }) => (
            <ListItem
              key={path}
              component={RouterLink}
              to={path}
              onClick={() => setDrawerOpen(false)}
              sx={{
                color: isActive(path) ? 'text.primary' : 'text.secondary',
                borderLeft: '3px solid',
                borderLeftColor: isActive(path) ? 'primary.main' : 'transparent',
                pl: 2.5,
                py: 1.5,
                textDecoration: 'none',
                '&:hover': { color: 'text.primary', bgcolor: 'rgba(0,0,0,0.04)' },
              }}
            >
              <ListItemText
                primary={title}
                primaryTypographyProps={{ fontWeight: isActive(path) ? 600 : 400, fontSize: '1rem' }}
              />
            </ListItem>
          ))}
        </List>
      </Drawer>
    </>
  );
};

export default Navbar;
